import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, Eye, Save, RefreshCw, Send, Server, Zap, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_TEMPLATES = {
  newsletter: {
    name: 'Newsletter',
    subject: 'SHRT Newsletter',
    greeting: 'Hallo [Name],',
    content: `Wir freuen uns, Ihnen die neuesten Nachrichten vom Swiss Historic Racing Team mitzuteilen.

**Kommende Events:**
• [Event 1]
• [Event 2]

Weitere Informationen finden Sie im Mitgliederbereich.`,
    signature: 'Freundliche Grüsse & happy motoring.',
    footer: 'Um keine weiteren Newsletter zu erhalten, kontaktieren Sie bitte info@shrt.ch'
  },
  'password-setup': {
    name: 'Passwort-Setup',
    subject: 'SHRT Mitgliederbereich - Passwort einrichten',
    greeting: 'Hallo [Name],',
    content: `Willkommen im SHRT Mitgliederbereich!

Bitte klicken Sie auf den Button unten, um Ihr Passwort einzurichten.

Dieser Link ist 24 Stunden gültig.`,
    signature: 'Freundliche Grüsse & happy motoring.',
    buttonText: 'PASSWORT EINRICHTEN'
  },
  'profile-change': {
    name: 'Profil-Änderung',
    subject: 'SHRT: Profil-Änderung von [Name]',
    greeting: '',
    content: `**Profil-Änderung**

**Mitglied:** [Name] ([Email])

| Feld | Alter Wert | Neuer Wert |
|------|------------|------------|
| [Feld] | [Alt] | [Neu] |

Diese Änderungen sind im Mitgliederbereich sofort sichtbar.
Bitte synchronisieren Sie die Daten bei Bedarf mit Monday.com.`,
    signature: 'Freundliche Grüsse & happy motoring.'
  }
};

function EmailProviderSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [testingResend, setTestingResend] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [senderName, setSenderName] = useState('');
  const [savingSender, setSavingSender] = useState(false);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/email-settings`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSettings(res.data);
      setSenderName(res.data.sender_name || 'S.H.R.T.');
    } catch { toast.error('Fehler beim Laden der E-Mail-Einstellungen'); }
    finally { setLoading(false); }
  };

  const setProvider = async (provider) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/email-settings`, { provider }, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
      });
      setSettings(prev => ({ ...prev, provider }));
      toast.success(`E-Mail Provider auf ${provider.toUpperCase()} gesetzt`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const saveSenderName = async () => {
    if (!senderName.trim()) return;
    setSavingSender(true);
    try {
      await axios.put(`${API_URL}/api/admin/email-settings`, { sender_name: senderName.trim() }, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
      });
      toast.success('Absendername gespeichert');
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
    finally { setSavingSender(false); }
  };

  const testProvider = async (type) => {
    const setTesting = type === 'smtp' ? setTestingSMTP : setTestingResend;
    setTesting(true);
    try {
      const body = testRecipient.trim() ? { to_email: testRecipient.trim() } : {};
      const res = await axios.post(`${API_URL}/api/admin/test-email-${type}`, body, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || `${type.toUpperCase()} Test fehlgeschlagen`);
    }
    finally { setTesting(false); }
  };

  if (loading) return <div className="py-4"><Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" /></div>;
  if (!settings) return null;

  const isSmtp = settings.provider === 'smtp';
  const isResend = settings.provider === 'resend';

  return (
    <div className="mb-8" data-testid="email-provider-settings">
      <h2 className="text-lg font-semibold mb-4 uppercase tracking-wider">E-Mail Provider</h2>
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Absendername</label>
          <div className="flex gap-2">
            <Input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="z.B. S.H.R.T."
              className="rounded-none text-sm"
              data-testid="sender-name-input"
            />
            <Button size="sm" variant="outline" onClick={saveSenderName} disabled={savingSender} className="rounded-none text-xs uppercase whitespace-nowrap" data-testid="save-sender-name-btn">
              {savingSender ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-1" />}
              {savingSender ? '' : 'Speichern'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">Wird als Absendername in allen E-Mails angezeigt</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Test-Empfänger</label>
          <Input
            type="email"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            placeholder="E-Mail für Tests (leer = eigene Adresse)"
            className="rounded-none text-sm"
            data-testid="provider-test-email-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* SMTP Card */}
        <div className={`border p-5 transition-all ${isSmtp ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-muted-foreground/40'}`} data-testid="smtp-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Server size={18} className={isSmtp ? 'text-primary' : 'text-muted-foreground'} />
              <span className="font-semibold text-sm uppercase tracking-wider">SMTP</span>
            </div>
            {isSmtp && <span className="text-xs bg-primary text-white px-2 py-0.5 font-medium uppercase">Aktiv</span>}
          </div>
          <div className="text-xs text-muted-foreground space-y-1 mb-4">
            <p>Server: <span className="text-foreground">{settings.smtp_host}</span></p>
            <p>Von: <span className="text-foreground">{settings.smtp_from}</span></p>
            <p>Status: {settings.smtp_configured ? (
              <span className="text-green-500 inline-flex items-center gap-1"><CheckCircle2 size={11} /> Konfiguriert</span>
            ) : (
              <span className="text-red-500 inline-flex items-center gap-1"><XCircle size={11} /> Nicht konfiguriert</span>
            )}</p>
          </div>
          <div className="flex gap-2">
            {!isSmtp && (
              <Button size="sm" onClick={() => setProvider('smtp')} disabled={saving} className="rounded-none text-xs uppercase bg-primary text-white" data-testid="activate-smtp-btn">
                Aktivieren
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => testProvider('smtp')} disabled={testingSMTP} className="rounded-none text-xs uppercase" data-testid="test-smtp-btn">
              {testingSMTP ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
              Test senden
            </Button>
          </div>
        </div>

        {/* Resend Card */}
        <div className={`border p-5 transition-all ${isResend ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-muted-foreground/40'}`} data-testid="resend-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap size={18} className={isResend ? 'text-primary' : 'text-muted-foreground'} />
              <span className="font-semibold text-sm uppercase tracking-wider">Resend</span>
            </div>
            {isResend && <span className="text-xs bg-primary text-white px-2 py-0.5 font-medium uppercase">Aktiv</span>}
          </div>
          <div className="text-xs text-muted-foreground space-y-1 mb-4">
            <p>Von: <span className="text-foreground">{settings.resend_from || '—'}</span></p>
            <p>Antwort an: <span className="text-foreground">{settings.resend_reply_to || '—'}</span></p>
            <p>Status: {settings.resend_configured ? (
              <span className="text-green-500 inline-flex items-center gap-1"><CheckCircle2 size={11} /> API Key vorhanden</span>
            ) : (
              <span className="text-red-500 inline-flex items-center gap-1"><XCircle size={11} /> Nicht konfiguriert</span>
            )}</p>
          </div>
          <div className="flex gap-2">
            {!isResend && (
              <Button size="sm" onClick={() => setProvider('resend')} disabled={saving || !settings.resend_configured} className="rounded-none text-xs uppercase bg-primary text-white" data-testid="activate-resend-btn">
                Aktivieren
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => testProvider('resend')} disabled={testingResend || !settings.resend_configured} className="rounded-none text-xs uppercase" data-testid="test-resend-btn">
              {testingResend ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
              Test senden
            </Button>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-3">
        Der aktive Provider wird für alle E-Mails verwendet (Passwort-Reset, Rechnungen, Newsletter etc.)
      </p>
    </div>
  );
}

export default function AdminEmailTemplatesPage() {
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState('newsletter');
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    if (user?.email) {
      setTestEmail(user.email);
    }
  }, [user]);

  const getToken = () => localStorage.getItem('shrt_token');

  const loadPreview = async (templateType) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/email-preview/${templateType}`);
      setPreviewHtml(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Vorschau konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (field, value) => {
    setTemplates(prev => ({
      ...prev,
      [selectedTemplate]: {
        ...prev[selectedTemplate],
        [field]: value
      }
    }));
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    
    const token = getToken();
    const template = templates[selectedTemplate];
    
    try {
      await axios.post(
        `${API_URL}/api/admin/test-email`,
        {
          to_email: testEmail,
          subject: `[TEST] ${template.subject}`,
          message: template.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test-E-Mail an ${testEmail} gesendet!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  const currentTemplate = templates[selectedTemplate];

  return (
    <Layout>
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Mail className="text-primary" size={28} />
          <h1 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-foreground uppercase tracking-tight">
            E-Mail Einstellungen
          </h1>
        </div>

        {/* Provider Settings */}
        <EmailProviderSettings />

        {/* Divider */}
        <div className="border-t border-border mb-8" />

        <h2 className="text-lg font-semibold mb-4 uppercase tracking-wider">E-Mail Templates</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Editor */}
          <div className="space-y-6">
            {/* Template Selector */}
            <div className="flex gap-2 flex-wrap">
              {Object.entries(templates).map(([key, template]) => (
                <Button
                  key={key}
                  variant={selectedTemplate === key ? 'default' : 'outline'}
                  onClick={() => {
                    setSelectedTemplate(key);
                    setShowPreview(false);
                  }}
                  className={`rounded-none uppercase text-xs tracking-wider ${
                    selectedTemplate === key ? 'bg-primary text-white' : ''
                  }`}
                  data-testid={`template-btn-${key}`}
                >
                  {template.name}
                </Button>
              ))}
            </div>

            {/* Template Fields */}
            <div className="bg-card border border-border p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Betreff
                </label>
                <Input
                  value={currentTemplate.subject}
                  onChange={(e) => handleTemplateChange('subject', e.target.value)}
                  className="mt-1 rounded-none"
                  data-testid="template-subject"
                />
              </div>

              {currentTemplate.greeting !== undefined && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Begrüssung
                  </label>
                  <Input
                    value={currentTemplate.greeting}
                    onChange={(e) => handleTemplateChange('greeting', e.target.value)}
                    className="mt-1 rounded-none"
                    placeholder="Hallo [Name],"
                    data-testid="template-greeting"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Inhalt
                </label>
                <Textarea
                  value={currentTemplate.content}
                  onChange={(e) => handleTemplateChange('content', e.target.value)}
                  className="mt-1 rounded-none min-h-[200px] font-mono text-sm"
                  data-testid="template-content"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variablen: [Name], [Email], [Jahr]
                </p>
              </div>

              {currentTemplate.buttonText && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Button Text
                  </label>
                  <Input
                    value={currentTemplate.buttonText}
                    onChange={(e) => handleTemplateChange('buttonText', e.target.value)}
                    className="mt-1 rounded-none"
                    data-testid="template-button"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  Signatur
                </label>
                <Input
                  value={currentTemplate.signature}
                  onChange={(e) => handleTemplateChange('signature', e.target.value)}
                  className="mt-1 rounded-none"
                  data-testid="template-signature"
                />
              </div>

              {currentTemplate.footer && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Footer Text
                  </label>
                  <Input
                    value={currentTemplate.footer}
                    onChange={(e) => handleTemplateChange('footer', e.target.value)}
                    className="mt-1 rounded-none"
                    data-testid="template-footer"
                  />
                </div>
              )}
            </div>

            {/* Test Email Section */}
            <div className="bg-muted/30 border border-border p-4">
              <label className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Test-E-Mail senden an (via aktiven Provider)
              </label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="E-Mail-Adresse"
                  className="rounded-none flex-1"
                  data-testid="test-email-input"
                />
                <Button
                  onClick={sendTestEmail}
                  variant="outline"
                  className="rounded-none uppercase tracking-wider"
                  data-testid="test-email-btn"
                >
                  <Send size={16} className="mr-2" />
                  Senden
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => loadPreview(selectedTemplate)}
                disabled={loading}
                variant="outline"
                className="rounded-none uppercase tracking-wider"
                data-testid="preview-btn"
              >
                <Eye size={16} className="mr-2" />
                Vorschau
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-card border border-border">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-foreground uppercase tracking-wider text-sm">
                Vorschau: {currentTemplate.name}
              </h3>
              <Button
                onClick={() => loadPreview(selectedTemplate)}
                variant="ghost"
                size="sm"
                disabled={loading}
                data-testid="refresh-preview"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </Button>
            </div>
            
            <div className="h-[600px] overflow-auto bg-white">
              {showPreview && previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0"
                  title="Email Preview"
                  data-testid="email-preview-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  <div className="text-center">
                    <Eye size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Klicken Sie auf "Vorschau" um das Template zu sehen</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-8 p-4 bg-muted/30 border border-border text-sm text-muted-foreground">
          <p className="font-semibold text-foreground mb-2">Hinweis:</p>
          <p>Die E-Mail-Templates werden aktuell im Code definiert. Änderungen hier dienen zur Vorschau und zum Testen. 
          Für permanente Änderungen kontaktieren Sie den Administrator.</p>
        </div>
      </div>
    </Layout>
  );
}
